/**
 * Dynamic Product Communication Helper
 * Facilitates communication between dynamic-featured-product and dynamic-imagewith-text sections
 */

// Initialize namespace
window.SpiralDirect = window.SpiralDirect || {};

// Debug mode - set to true for verbose logging
window.SpiralDirect.debug = true;

// EMERGENCY: Override window.location.href to prevent any code from causing redirects
// This is a drastic measure to prevent infinite reload loops
(function() {
  try {
    const originalDefineProperty = Object.defineProperty;
    
    // Create a proxy for window.location that prevents redirects
    originalDefineProperty(window, 'location', {
      get: function() {
        const originalLocation = window.location || document.location;
        
        // Create a proxy for the href property
        const locationProxy = new Proxy(originalLocation, {
          set: function(obj, prop, value) {
            if (prop === 'href') {
              console.warn('[SpiralDirect] BLOCKED page redirect attempt:', value);
              return true; // Pretend we set it, but do nothing
            }
            obj[prop] = value;
            return true;
          }
        });
        
        return locationProxy;
      },
      configurable: false
    });
  } catch (e) {
    console.error('[SpiralDirect] Failed to override redirect protection:', e);
  }
})();

/**
 * Log helper that only outputs when debug is enabled
 */
window.SpiralDirect.log = function() {
  if (window.SpiralDirect.debug) {
    console.log('[SpiralDirect]', ...arguments);
  }
};

/**
 * Initialize product communication system
 */
window.SpiralDirect.initProductCommunication = function() {
  window.SpiralDirect.log('Initializing product communication system');
  
  // IMPORTANT: Clear any stored redirection flags that might cause loops
  window.SpiralDirect.clearRedirectFlags();
  
  // Store featured product information in a global object
  window.spiralDirectFeaturedProduct = window.spiralDirectFeaturedProduct || {};
  
  // Set up event listeners for product updates
  document.addEventListener('spiral:featured-product-updated', function(event) {
    window.SpiralDirect.log('Product update event received:', event.detail);
    
    // Store the latest product info
    window.spiralDirectFeaturedProduct = event.detail;
    
    // Check if the product has tags
    if (event.detail.tags && Array.isArray(event.detail.tags)) {
      window.SpiralDirect.log('Product tags:', event.detail.tags);
    } else {
      window.SpiralDirect.log('No tags found in product update event');
      
      // If no tags provided, try to fetch the product data
      if (event.detail.handle) {
        window.SpiralDirect.fetchProductData(event.detail.handle);
      }
    }
  });
  
  // Re-dispatch an event if already have product data, but avoid infinite loops
  if (window.spiralDirectFeaturedProduct && window.spiralDirectFeaturedProduct.handle) {
    // Check if the URL already contains the product handle to prevent redirect loops
    const urlParams = new URLSearchParams(window.location.search);
    const urlProductHandle = urlParams.get('product_handle');
    
    // Only re-dispatch if not already showing this product or if in design mode
    const isDesignMode = window.Shopify && window.Shopify.designMode;
    
    if (isDesignMode || !urlProductHandle || urlProductHandle !== window.spiralDirectFeaturedProduct.handle) {
      window.SpiralDirect.log('Found existing product data, re-dispatching event');
      setTimeout(function() {
        document.dispatchEvent(new CustomEvent('spiral:featured-product-updated', {
          detail: window.spiralDirectFeaturedProduct
        }));
      }, 100);
    } else {
      window.SpiralDirect.log('URL already contains product handle, skipping re-dispatch');
    }
  }
};

/**
 * Helper function to clear all redirection flags from session storage
 */
window.SpiralDirect.clearRedirectFlags = function() {
  // Find all keys in sessionStorage that might cause redirect loops
  const flagsToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.startsWith('spiral_product_refresh_') || key.startsWith('spiral_product_loaded_'))) {
      flagsToRemove.push(key);
    }
  }
  
  // Remove all flags
  flagsToRemove.forEach(key => {
    window.SpiralDirect.log('Clearing redirect flag:', key);
    sessionStorage.removeItem(key);
  });
};

/**
 * Fetch product data including tags
 */
window.SpiralDirect.fetchProductData = function(productHandle) {
  if (!productHandle) return;
  
  // IMPORTANT: To stop the redirect loop, clear any stored flags for this product
  sessionStorage.removeItem('spiral_product_loaded_' + productHandle);
  sessionStorage.removeItem('spiral_product_refresh_' + productHandle);
  
  window.SpiralDirect.log('Fetching product data for:', productHandle);
  
  // Check if we already have this product's data in the page URL
  const urlParams = new URLSearchParams(window.location.search);
  const urlProductHandle = urlParams.get('product_handle');
  const alreadyInUrl = urlProductHandle === productHandle;
  
  fetch(`/products/${productHandle}.js`)
    .then(response => response.json())
    .then(productData => {
      window.SpiralDirect.log('Product data retrieved:', productData);
      
      // Update the global object with complete product data
      window.spiralDirectFeaturedProduct = window.spiralDirectFeaturedProduct || {};
      window.spiralDirectFeaturedProduct.tags = productData.tags;
      window.spiralDirectFeaturedProduct.id = productData.id;
      window.spiralDirectFeaturedProduct.title = productData.title;
      window.spiralDirectFeaturedProduct.handle = productData.handle;
      
      // We're completely disabling the page reload approach and using only client-side updates
      // The page should never reload automatically, that was causing the infinite loop
      
      // Re-dispatch the event with complete data to trigger client-side updates
      document.dispatchEvent(new CustomEvent('spiral:featured-product-updated', {
        detail: {
          id: productData.id,
          handle: productData.handle,
          title: productData.title,
          tags: productData.tags
        }
      }));
    })
    .catch(error => {
      console.error('Error fetching product data:', error);
    });
};
  
/**
 * Tag matching helper
 */
window.SpiralDirect.matchTag = function(productTags, tagToMatch) {
  if (!productTags || !Array.isArray(productTags)) return false;
  
  // Normalize the tag to match - remove # prefix and trim spaces
  const normalizedTagToMatch = tagToMatch.replace(/^#/, '').toLowerCase().trim().replace(/\s+/g, ' ');
  
  window.SpiralDirect.log(`Looking for tag: "${normalizedTagToMatch}" (normalized from "${tagToMatch}")`);
  
  // Check each product tag for a match
  return productTags.some(tag => {
    // Normalize product tag - remove # prefix and trim spaces
    const normalizedTag = tag.replace(/^#/, '').toLowerCase().trim().replace(/\s+/g, ' ');
    
    // Try exact match first
    let isMatch = normalizedTag === normalizedTagToMatch;
    
    // Keep track of all variations we've tried
    const triedVariations = [normalizedTag];
    
    // If no exact match, try without the "Label" suffix if present
    if (!isMatch && normalizedTagToMatch.endsWith(' label')) {
      const withoutLabel = normalizedTagToMatch.replace(/ label$/, '');
      triedVariations.push(withoutLabel);
      triedVariations.push(withoutLabel + ' label');
      isMatch = normalizedTag === withoutLabel || normalizedTag === withoutLabel + ' label';
    }
    
    // Also try with the word "label" added if it's not there
    if (!isMatch && !normalizedTagToMatch.endsWith(' label')) {
      const withLabel = normalizedTagToMatch + ' label';
      triedVariations.push(withLabel);
      isMatch = normalizedTag === withLabel;
    }
    
    // Try with case variations of "Label" vs "label"
    if (!isMatch && normalizedTag.toLowerCase().replace(/\blabel\b/, '') === normalizedTagToMatch.toLowerCase().replace(/\blabel\b/, '')) {
      window.SpiralDirect.log(`Matched with case-insensitive label comparison: "${tag}" vs "${tagToMatch}"`);
      isMatch = true;
    }
    
    // Try partial match (e.g., "spiritual" matches "spiritual label")
    if (!isMatch && (normalizedTag.includes(normalizedTagToMatch) || normalizedTagToMatch.includes(normalizedTag))) {
      // Only consider it a match if it's a whole word match or if one is fully contained within the other
      const isContained = normalizedTag.includes(normalizedTagToMatch) || normalizedTagToMatch.includes(normalizedTag);
      if (isContained) {
        window.SpiralDirect.log(`Matched with partial tag comparison: "${tag}" vs "${tagToMatch}"`);
        isMatch = true;
      }
    }
    
    if (isMatch) {
      window.SpiralDirect.log(`✅ MATCH FOUND! "${tag}" (${normalizedTag}) matches "${tagToMatch}" (${normalizedTagToMatch})`);
    } else {
      window.SpiralDirect.log(`❌ No match: "${tag}" (${normalizedTag}) vs "${tagToMatch}" (${normalizedTagToMatch})`);
      window.SpiralDirect.log(`   Tried variations: ${triedVariations.join(', ')}`);
    }
    
    return isMatch;
  });
};

/**
 * Helper function to resolve image IDs based on tag mappings
 * Returns the best matching image ID from the available options
 */
window.SpiralDirect.resolveImageId = function(imageHandleId, availableImageIds) {
  if (!imageHandleId || !availableImageIds || !Array.isArray(availableImageIds)) {
    return null;
  }
  
  // Check direct match first
  if (availableImageIds.includes(imageHandleId)) {
    return imageHandleId;
  }
  
  // Try variations
  const variations = [
    imageHandleId, 
    imageHandleId + '_image', 
    imageHandleId.replace(/_image$/, ''),
    imageHandleId.toLowerCase(),
    imageHandleId.toLowerCase() + '_image',
    imageHandleId.replace(/_/g, '-'),
    imageHandleId.replace(/-/g, '_')
  ];
  
  // Find the first variation that exists in availableImageIds
  for (const variant of variations) {
    if (availableImageIds.includes(variant)) {
      window.SpiralDirect.log(`Resolved image ID "${imageHandleId}" to "${variant}"`);
      return variant;
    }
  }
  
  // If no direct variation match, look for partial matches
  for (const variant of variations) {
    const partialMatch = availableImageIds.find(id => id.includes(variant) || variant.includes(id));
    if (partialMatch) {
      window.SpiralDirect.log(`Found partial image ID match: "${imageHandleId}" → "${partialMatch}"`);
      return partialMatch;
    }
  }
  
  // No match found
  return null;
};

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', function() {
  // Check for reload loop condition and break it
  const reloadCount = parseInt(sessionStorage.getItem('spiral_reload_count') || '0', 10);
  if (reloadCount > 3) {
    // We're probably in a reload loop, so remove all parameters from URL
    const currentPath = window.location.pathname;
    if (window.location.search && window.location.search.includes('product_handle=')) {
      console.warn('[SpiralDirect] Breaking detected reload loop by removing URL parameters');
      sessionStorage.clear(); // Clear all session storage
      history.replaceState({}, '', currentPath);
      return; // Don't initialize to break the loop
    }
  } else {
    // Increment reload counter (will be cleared on next page load if not a reload loop)
    sessionStorage.setItem('spiral_reload_count', (reloadCount + 1).toString());
    setTimeout(() => {
      // Reset the counter after a delay - if the page hasn't reloaded again, it's not a loop
      sessionStorage.setItem('spiral_reload_count', '0');
    }, 5000);
  }
  
  window.SpiralDirect.initProductCommunication();
  window.SpiralDirect.log('Product communication system ready');
});
